/**
 * Strategy-tinted recommended-drill hero card.
 *
 * Used by both the Drills home and the Skills screen — keeping one
 * implementation means the visual treatment of "weakest" / "unmapped" /
 * "regression" / "fundamentals" recommendations stays consistent
 * everywhere the user encounters them.
 *
 * Palette mirrors iOS RecommendedDrillCard.strategyTint.
 */

import { ChevronRight, ICON_STROKE, Sparkles } from "@/shared/components/icons";
import { DrillLaunchLink } from "@/shared/components/DrillLaunchLink";
import { drillFromRecommendation } from "@/shared/chatRoute";
import type { RecommendedDrill } from "@/shared/models";

interface Palette {
  /** RGB triplet used in rgba() gradients + shadows. */
  rgb: string;
  /** Strategy-specific eyebrow label shown above the skill name. */
  label: string;
  /** Tailwind border-color class for the card outline. */
  ring: string;
  /** Tailwind background-color class for the CTA button. */
  btn: string;
  /** CTA button label — "Start drilling" across all strategies. */
  cta: string;
}

const STRATEGY_PALETTE: Record<string, Palette> = {
  weakest: {
    rgb: "124,147,241", // accent blue
    label: "Sharpen weak spot",
    ring: "border-accent/30",
    btn: "bg-accent hover:bg-accent/90",
    cta: "Start drilling",
  },
  unmapped: {
    rgb: "168,85,247", // purple-500
    label: "Map a new area",
    ring: "border-purple-500/30",
    btn: "bg-purple-500 hover:bg-purple-500/90",
    cta: "Start drilling",
  },
  regression: {
    rgb: "74,222,128", // green-400
    label: "Test what's locked in",
    ring: "border-green-400/30",
    btn: "bg-green-500 hover:bg-green-500/90",
    cta: "Start drilling",
  },
  fundamentals: {
    rgb: "251,146,60", // orange-400
    label: "Build the base",
    ring: "border-orange-400/30",
    btn: "bg-orange-500 hover:bg-orange-500/90",
    cta: "Start drilling",
  },
};

export function RecommendedDrillCard({ drill }: { drill: RecommendedDrill }) {
  const palette = STRATEGY_PALETTE[drill.strategy] ?? STRATEGY_PALETTE.fundamentals!;
  const rgb = palette.rgb;

  return (
    <section
      className={`relative rounded-2xl border ${palette.ring} p-5 overflow-hidden`}
      style={{
        background: `linear-gradient(135deg, rgba(${rgb},0.16) 0%, rgba(${rgb},0.05) 60%, rgba(${rgb},0.02) 100%)`,
        boxShadow: `0 1px 0 rgba(255,255,255,0.06) inset, 0 24px 48px -28px rgba(${rgb},0.45)`,
      }}
    >
      {/* Soft accent glow in the top-right corner */}
      <div
        aria-hidden
        className="absolute -top-16 -right-16 w-48 h-48 rounded-full"
        style={{
          background: `radial-gradient(circle, rgba(${rgb},0.25) 0%, rgba(${rgb},0) 65%)`,
          filter: "blur(8px)",
        }}
      />
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          <span
            className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em]"
            style={{ color: `rgb(${rgb})` }}
          >
            <Sparkles size={12} strokeWidth={ICON_STROKE} />
            {palette.label}
          </span>
          <span className="text-[10px] uppercase tracking-[0.12em] text-text-muted/80">
            {drill.strategy}
          </span>
        </div>
        <div className="font-semibold text-text text-lg tracking-tight">
          {drill.skillName}
        </div>
        <p className="text-sm text-text-muted mt-2 leading-relaxed">
          {drill.rationale}
        </p>
        <DrillLaunchLink
          to={drillFromRecommendation(drill)}
          className={`inline-flex items-center gap-1.5 mt-4 px-4 py-3 rounded-lg text-white text-sm font-medium transition ${palette.btn}`}
          style={{
            boxShadow: `0 1px 0 rgba(255,255,255,0.18) inset, 0 10px 24px -10px rgba(${rgb},0.6)`,
          }}
        >
          {palette.cta}
          <ChevronRight size={14} strokeWidth={ICON_STROKE} />
        </DrillLaunchLink>
      </div>
    </section>
  );
}
